import { useState } from 'react';
import { ethers } from 'ethers';
import { BRIDGE_ABI, BRIDGE_ERC20_ABI } from '@/contracts/config';
import { resolveConnectedProvider } from '@coti-io/coti-wallet-plugin';
import { POD_PORTAL_ADMIN_ABI } from '@/contracts/pod';
import { useAccount, useSwitchChain } from 'wagmi';
import { useCotiPluginContext } from '@/context/PrivacyBridgeContext';
import { toast } from 'sonner';
import { formatAdminError } from '@/lib/formatAdminError';

/** Deployed PoD portals use uint128.max as the "no max fee cap" value. */
const POD_NO_MAX_FEE_SENTINEL = (1n << 128n) - 1n;

type PodAdminMethod =
  | 'setDepositFee'
  | 'setWithdrawFee'
  | 'setLimits'
  | 'pause'
  | 'unpause'
  | 'setIsDepositEnabled'
  | 'withdrawPortalFees'
  | 'rescueNative'
  | 'rescueERC20';

export const useBridgeAdmin = (onTransactionConfirmed?: () => void) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const { switchChainAsync } = useSwitchChain();
  const { switchNetwork } = useCotiPluginContext();
  // Connector/address of the wallet connected via RainbowKit — signing must go
  // through its provider, not window.ethereum, which another installed wallet
  // extension may own and pop up despite never being connected to the app.
  const { connector, address } = useAccount();

  const getProvider = async () => {
    if (!address) {
      throw new Error('Connect your wallet first');
    }

    const eip1193 = await resolveConnectedProvider(connector);
    if (!eip1193) {
      throw new Error('No wallet provider found');
    }
    return new ethers.BrowserProvider(eip1193);
  };

  /** Ensures the wallet is on the expected chain before signing (PoD portals live on Sepolia/Fuji). */
  const ensureWalletChain = async (expectedChainId: number) => {
    let provider = await getProvider();
    const network = await provider.getNetwork();
    if (Number(network.chainId) !== expectedChainId) {
      try {
        await switchChainAsync({ chainId: expectedChainId });
      } catch {
        const switched = await switchNetwork('0x' + expectedChainId.toString(16));
        if (!switched) {
          throw new Error('Please switch your wallet to the portal chain first');
        }
      }
      provider = await getProvider();
    }
    return provider;
  };

  const sendPodAdminTx = async (
    chainId: number,
    portalAddress: string,
    method: PodAdminMethod,
    args: unknown[],
    successMessage: string,
  ) => {
    setIsProcessing(true);
    try {
      const provider = await ensureWalletChain(chainId);
      const signer = await provider.getSigner(address);
      const portal = new ethers.Contract(portalAddress, POD_PORTAL_ADMIN_ABI, signer);

      // Pre-flight: reverts here (no gas spent) on missing role or bad params
      await portal[method].staticCall(...args);

      const tx = await portal[method](...args);
      toast.info('Transaction submitted. Waiting for confirmation...');

      const receipt = await tx.wait();
      toast.success(successMessage);

      if (onTransactionConfirmed && receipt.status === 1) {
        onTransactionConfirmed();
      }
    } catch (error) {
      console.error(`Error calling ${method}:`, error);
      // Avoid duplicate toast when the modal already surfaces formatAdminError(err)
      throw error;
    } finally {
      setIsProcessing(false);
    }
  };

  /** Map the UI's "0 = no cap" convention to the portal's uint128.max sentinel. */
  const toPodMaxFee = (maxFee: string) =>
    BigInt(maxFee) === 0n ? POD_NO_MAX_FEE_SENTINEL : BigInt(maxFee);

  const podSetDepositFee = (chainId: number, portalAddress: string, fixedFee: string, percentageBps: string, maxFee: string) =>
    sendPodAdminTx(chainId, portalAddress, 'setDepositFee',
      [BigInt(fixedFee), BigInt(percentageBps), toPodMaxFee(maxFee)],
      'Deposit fee updated successfully!');

  const podSetWithdrawFee = (chainId: number, portalAddress: string, fixedFee: string, percentageBps: string, maxFee: string) =>
    sendPodAdminTx(chainId, portalAddress, 'setWithdrawFee',
      [BigInt(fixedFee), BigInt(percentageBps), toPodMaxFee(maxFee)],
      'Withdraw fee updated successfully!');

  const podSetLimits = (
    chainId: number,
    portalAddress: string,
    minDeposit: string,
    maxDeposit: string,
    minWithdraw: string,
    maxWithdraw: string,
    tokenDecimals: number,
  ) =>
    sendPodAdminTx(chainId, portalAddress, 'setLimits',
      [
        ethers.parseUnits(minDeposit, tokenDecimals),
        ethers.parseUnits(maxDeposit, tokenDecimals),
        ethers.parseUnits(minWithdraw, tokenDecimals),
        ethers.parseUnits(maxWithdraw, tokenDecimals),
      ],
      'Limits updated successfully!');

  const podPause = (chainId: number, portalAddress: string) =>
    sendPodAdminTx(chainId, portalAddress, 'pause', [], 'Contract paused successfully!');

  const podUnpause = (chainId: number, portalAddress: string) =>
    sendPodAdminTx(chainId, portalAddress, 'unpause', [], 'Contract unpaused successfully!');

  const podSetDepositEnabled = (chainId: number, portalAddress: string, enabled: boolean) =>
    sendPodAdminTx(chainId, portalAddress, 'setIsDepositEnabled', [enabled],
      enabled ? 'Deposits enabled successfully!' : 'Deposits disabled successfully!');

  const podWithdrawPortalFees = (chainId: number, portalAddress: string, amount: string) =>
    sendPodAdminTx(chainId, portalAddress, 'withdrawPortalFees',
      [ethers.parseEther(amount)],
      'Portal fees rescued successfully!');

  const podRescueNative = (chainId: number, portalAddress: string, amount: string, decimals: number = 18) =>
    sendPodAdminTx(chainId, portalAddress, 'rescueNative',
      [ethers.parseUnits(amount, decimals)],
      'Tokens rescued successfully!');

  const podRescueErc20 = (
    chainId: number,
    portalAddress: string,
    tokenAddress: string,
    amount: string,
    decimals: number,
  ) =>
    sendPodAdminTx(chainId, portalAddress, 'rescueERC20',
      [tokenAddress, ethers.parseUnits(amount, decimals)],
      'Tokens rescued successfully!');

  const setDepositFee = async (bridgeAddress: string, fixedFee: string, percentageBps: string, maxFee: string, isNativeBridge: boolean) => {
    setIsProcessing(true);
    try {
      const provider = await getProvider();
      const signer = await provider.getSigner(address);
      const abi = isNativeBridge ? BRIDGE_ABI : BRIDGE_ERC20_ABI;
      const contract = new ethers.Contract(bridgeAddress, abi, signer);

      const tx = await contract.setDepositDynamicFee(fixedFee, percentageBps, maxFee);
      toast.info('Transaction submitted. Waiting for confirmation...');
      
      const receipt = await tx.wait();
      toast.success('Deposit fee updated successfully!');
      
      if (onTransactionConfirmed && receipt.status === 1) {
        onTransactionConfirmed();
      }
    } catch (error) {
      console.error('Error setting deposit fee:', error);
      toast.error(formatAdminError(error, 'Failed to set deposit fee'));
      throw error;
    } finally {
      setIsProcessing(false);
    }
  };

  const setWithdrawFee = async (bridgeAddress: string, fixedFee: string, percentageBps: string, maxFee: string, isNativeBridge: boolean) => {
    setIsProcessing(true);
    try {
      const provider = await getProvider();
      const signer = await provider.getSigner(address);
      const abi = isNativeBridge ? BRIDGE_ABI : BRIDGE_ERC20_ABI;
      const contract = new ethers.Contract(bridgeAddress, abi, signer);

      const tx = await contract.setWithdrawDynamicFee(fixedFee, percentageBps, maxFee);
      toast.info('Transaction submitted. Waiting for confirmation...');
      
      const receipt = await tx.wait();
      toast.success('Withdraw fee updated successfully!');
      
      if (onTransactionConfirmed && receipt.status === 1) {
        onTransactionConfirmed();
      }
    } catch (error) {
      console.error('Error setting withdraw fee:', error);
      toast.error(formatAdminError(error, 'Failed to set withdraw fee'));
      throw error;
    } finally {
      setIsProcessing(false);
    }
  };

  const setLimits = async (bridgeAddress: string, minDeposit: string, maxDeposit: string, minWithdraw: string, maxWithdraw: string, isNativeBridge: boolean, tokenDecimals: number) => {
    setIsProcessing(true);
    try {
      const provider = await getProvider();
      const signer = await provider.getSigner(address);
      const abi = isNativeBridge ? BRIDGE_ABI : BRIDGE_ERC20_ABI;
      const contract = new ethers.Contract(bridgeAddress, abi, signer);

      const tx = await contract.setLimits(
        ethers.parseUnits(minDeposit, tokenDecimals),
        ethers.parseUnits(maxDeposit, tokenDecimals),
        ethers.parseUnits(minWithdraw, tokenDecimals),
        ethers.parseUnits(maxWithdraw, tokenDecimals)
      );
      toast.info('Transaction submitted. Waiting for confirmation...');

      const receipt = await tx.wait();
      toast.success('Limits updated successfully!');

      if (onTransactionConfirmed && receipt.status === 1) {
        onTransactionConfirmed();
      }
    } catch (error) {
      console.error('Error setting limits:', error);
      toast.error(formatAdminError(error, 'Failed to set limits'));
      throw error;
    } finally {
      setIsProcessing(false);
    }
  };

  const rescueCotiFees = async (bridgeAddress: string, amount: string, isNativeBridge: boolean) => {
    setIsProcessing(true);
    try {
      const provider = await getProvider();
      const signer = await provider.getSigner(address);
      const abi = isNativeBridge ? BRIDGE_ABI : BRIDGE_ERC20_ABI;
      const contract = new ethers.Contract(bridgeAddress, abi, signer);

      // Funds always go to the contract's feeRecipient — no recipient arg on-chain.
      const amountInWei = ethers.parseEther(amount);
      const tx = await contract.withdrawCotiFees(amountInWei);
      toast.info('Transaction submitted. Waiting for confirmation...');

      const receipt = await tx.wait();
      toast.success('COTI fees rescued successfully!');

      if (onTransactionConfirmed && receipt.status === 1) {
        onTransactionConfirmed();
      }
    } catch (error) {
      console.error('Error rescuing COTI fees:', error);
      toast.error(formatAdminError(error, 'Failed to rescue COTI fees'));
      throw error;
    } finally {
      setIsProcessing(false);
    }
  };

  const rescueNative = async (
    bridgeAddress: string,
    amount: string,
    isNativeBridge: boolean,
    decimals: number = 18,
    tokenAddress?: string,
  ) => {
    setIsProcessing(true);
    try {
      const provider = await getProvider();
      const signer = await provider.getSigner(address);
      const abi = isNativeBridge ? BRIDGE_ABI : BRIDGE_ERC20_ABI;
      const contract = new ethers.Contract(bridgeAddress, abi, signer);

      // Funds always go to the contract's rescueRecipient — no recipient arg on-chain.
      const amountInWei = ethers.parseUnits(amount, decimals);

      let tx;
      if (isNativeBridge) {
        tx = await contract.rescueNative(amountInWei);
      } else {
        if (!tokenAddress) {
          throw new Error('Token address is required for ERC20 bridge rescue');
        }
        tx = await contract.rescueERC20(tokenAddress, amountInWei);
      }

      toast.info('Transaction submitted. Waiting for confirmation...');

      const receipt = await tx.wait();
      toast.success('Tokens rescued successfully!');

      if (onTransactionConfirmed && receipt.status === 1) {
        onTransactionConfirmed();
      }
    } catch (error) {
      console.error('Error rescuing tokens:', error);
      toast.error(formatAdminError(error, 'Failed to rescue tokens'));
      throw error;
    } finally {
      setIsProcessing(false);
    }
  };

  const pauseContract = async (bridgeAddress: string, isNativeBridge: boolean) => {
    setIsProcessing(true);
    try {
      const provider = await getProvider();
      const signer = await provider.getSigner(address);
      const abi = isNativeBridge ? BRIDGE_ABI : BRIDGE_ERC20_ABI;
      const contract = new ethers.Contract(bridgeAddress, abi, signer);

      const tx = await contract.pause();
      toast.info('Transaction submitted. Waiting for confirmation...');
      
      const receipt = await tx.wait();
      toast.success('Contract paused successfully!');
      
      // Call the callback after transaction is confirmed
      if (onTransactionConfirmed && receipt.status === 1) {
        onTransactionConfirmed();
      }
    } catch (error) {
      console.error('Error pausing contract:', error);
      toast.error(formatAdminError(error, 'Failed to pause contract'));
      throw error;
    } finally {
      setIsProcessing(false);
    }
  };

  const unpauseContract = async (bridgeAddress: string, isNativeBridge: boolean) => {
    setIsProcessing(true);
    try {
      const provider = await getProvider();
      const signer = await provider.getSigner(address);
      const abi = isNativeBridge ? BRIDGE_ABI : BRIDGE_ERC20_ABI;
      const contract = new ethers.Contract(bridgeAddress, abi, signer);

      const tx = await contract.unpause();
      toast.info('Transaction submitted. Waiting for confirmation...');
      
      const receipt = await tx.wait();
      toast.success('Contract unpaused successfully!');
      
      // Call the callback after transaction is confirmed
      if (onTransactionConfirmed && receipt.status === 1) {
        onTransactionConfirmed();
      }
    } catch (error) {
      console.error('Error unpausing contract:', error);
      toast.error(formatAdminError(error, 'Failed to unpause contract'));
      throw error;
    } finally {
      setIsProcessing(false);
    }
  };

  const setIsDepositEnabled = async (bridgeAddress: string, enabled: boolean, isNativeBridge: boolean) => {
    setIsProcessing(true);
    try {
      const provider = await getProvider();
      const signer = await provider.getSigner(address);
      const abi = isNativeBridge ? BRIDGE_ABI : BRIDGE_ERC20_ABI;
      const contract = new ethers.Contract(bridgeAddress, abi, signer);

      const tx = await contract.setIsDepositEnabled(enabled);
      toast.info('Transaction submitted. Waiting for confirmation...');

      const receipt = await tx.wait();
      toast.success(enabled ? 'Deposits enabled successfully!' : 'Deposits disabled successfully!');

      if (onTransactionConfirmed && receipt.status === 1) {
        onTransactionConfirmed();
      }
    } catch (error) {
      console.error('Error setting deposit enabled:', error);
      toast.error(formatAdminError(error, 'Failed to update deposit status'));
      throw error;
    } finally {
      setIsProcessing(false);
    }
  };

  return {
    isProcessing,
    setDepositFee,
    setWithdrawFee,
    podSetDepositFee,
    podSetWithdrawFee,
    podSetLimits,
    podPause,
    podUnpause,
    podSetDepositEnabled,
    podWithdrawPortalFees,
    podRescueNative,
    podRescueErc20,
    setLimits,
    rescueCotiFees,
    rescueNative,
    pauseContract,
    unpauseContract,
    setIsDepositEnabled,
  };
};
